import React, { FC, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import FeatherIcon from "feather-icons-react";
import { useSnackbar } from "notistack";
import { parse, unparse } from "papaparse";
import {
  DoubleMaterialityStakeholdersControllerGetAllStakeholders200Response,
  StakeholderDto,
  LocalizationLanguage,
  DoubleMaterialityStakeholderType,
  DoubleMaterialityStakeholderImpactArea,
  DoubleMaterialityStakeholderESRSArea,
} from "@green0meter/api-client";
import {
  DataGrid,
  GridColDef,
  GridRenderCellParams,
  GridToolbarContainer,
  GridToolbarFilterButton,
  GridToolbarQuickFilter,
  GridLogicOperator,
  getGridSingleSelectOperators,
  GridFilterItem,
} from "@mui/x-data-grid";

import DataPlaceholder from "../StakeholdersTableDataPlaceholder";
import { colorMap, ESRS_TYPES, labelMap } from "./StakeholdersTable.constants";

import {
  Chip as MuiChip,
  IconButton,
  Stack,
  Tooltip,
  Button,
  Box,
} from "@components/base";
import { Chip } from "@components/common";
import { useUserStore } from "@store/user";
import { useGetStakeholders } from "@api";
import { useImportDMStakeholders } from "@api/hooks/doubleMateriality/ImportDMStakeholder/useImportDMStakeholders";
import { PLATFORM_LANGUAGES } from "@constants";

interface IStakeholders {
  onStakeholderEdit?: (stakeholder: StakeholderDto) => void;
  onStakeholderDelete?: (stakeholder: StakeholderDto) => void;
  onAddStakeholder?: () => void;
}

export type StakeholderItemType =
  DoubleMaterialityStakeholdersControllerGetAllStakeholders200Response["items"][0];

const iroFilterOperators = getGridSingleSelectOperators()
  .filter((operator) => operator.value === 'is' || operator.value === 'not')
  .map((operator) => {
    return {
      ...operator,
      getApplyFilterFn: (filterItem: GridFilterItem) => {
        if (!filterItem.field || !filterItem.value || !filterItem.operator) {
          return null;
        }

        return (value: DoubleMaterialityStakeholderImpactArea[]) => {
          if (!Array.isArray(value)) return false;
          if (operator.value === 'is') {
            return value.includes(filterItem.value);
          }
          if (operator.value === 'not') {
            return !value.includes(filterItem.value);
          }

          return false;
        };
      },
    };
  });

const esrsFilterOperators = getGridSingleSelectOperators()
  .filter((operator) => operator.value === "is" || operator.value === "not")
  .map((operator) => {
    return {
      ...operator,
      getApplyFilterFn: (filterItem: GridFilterItem) => {
        if (!filterItem.field || !filterItem.value || !filterItem.operator) {
          return null;
        }

        return (value: DoubleMaterialityStakeholderESRSArea[]) => {
          if (!Array.isArray(value)) return false;
          if (operator.value === "is") {
            return value.includes(filterItem.value as DoubleMaterialityStakeholderESRSArea);
          }
          if (operator.value === "not") {
            return !value.includes(filterItem.value as DoubleMaterialityStakeholderESRSArea);
          }

          return false;
        };
      },
    };
  });

const StakeholdersTable: FC<IStakeholders> = ({
  onStakeholderEdit,
  onStakeholderDelete,
  onAddStakeholder,
}) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const { user } = useUserStore();
  const [searchParams] = useSearchParams();
  const year = parseInt(
    searchParams.get("year") || String(new Date().getFullYear()),
    10,
  );

  const fileInputRef = useRef<HTMLInputElement>(null);

  const { stakeholders, isPendingGetStakeholders } = useGetStakeholders(user, {
    year,
    pageSize: 1000,
    pageNumber: 1,
  });

  const { importStakeholders, isPendingImportStakeholders } =
    useImportDMStakeholders(user);

  const getImpactAreasSortValue = (
    impactAreas: DoubleMaterialityStakeholderImpactArea[] | null | undefined,
  ): string =>
    (impactAreas ?? [])
      .map((area) => labelMap[area] ?? String(area))
      .sort((a, b) => a.localeCompare(b))
      .join(", ");

  const getEsrsAreasSortValue = (
    esrsAreas: DoubleMaterialityStakeholderESRSArea[] | null | undefined,
  ): string =>
    (esrsAreas ?? [])
      .map((area) => ESRS_TYPES[area] ?? String(area))
      .sort((a, b) => a.localeCompare(b))
      .join(", ");

  const rows = stakeholders?.data ?? [];

  const handleStakeholderEdit = (stakeholder: StakeholderItemType): void => {
    onStakeholderEdit?.(stakeholder);
  };

  const handleStakeholderDelete = (stakeholder: StakeholderDto): void => {
    onStakeholderDelete?.(stakeholder);
  };

  const handleCopySurveyLink = async (
    stakeholder: StakeholderDto,
  ): Promise<void> => {
    const url = `${window.location.origin}/dm-survey/${stakeholder.id}`;

    try {
      await navigator.clipboard.writeText(url);
      enqueueSnackbar(t("common.copied"), { variant: "success" });
    } catch {
      enqueueSnackbar(t("common.error"), { variant: "error" });
    }
  };

  const handleExportCSV = (): void => {
    if (!stakeholders?.data.length) {
      enqueueSnackbar(t("stakeholders.export.noData", "No data to export"), {
        variant: "warning",
      });

      return;
    }

    const csvData = stakeholders.data.map((stakeholder: StakeholderItemType) => ({
      "Full Name": stakeholder.fullName,
      Email: stakeholder.email,
      "Company Name": stakeholder.companyName,
      Type: stakeholder.type,
      Comment: stakeholder.comment,
      Language: renderLanguage(stakeholder.language),
      "Impact Areas": stakeholder.impactAreas.join(", "),
      "ESRS Areas": stakeholder.esrsAreas.join(", "),
    }));

    const csv = unparse(csvData);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");

    if (link.download !== undefined) {
      const url = URL.createObjectURL(blob);

      link.setAttribute("href", url);
      link.setAttribute(
        "download",
        `stakeholders-${new Date().toISOString().split("T")[0]}.csv`,
      );
      link.style.visibility = "hidden";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      enqueueSnackbar(t("stakeholders.export.success", "Export successful"), {
        variant: "success",
      });
    }
  };

  const handleImportClick = (): void => {
    fileInputRef.current?.click();
  };

  const transformCsvDataToStakeholders = (
    csvData: Record<string, string>[],
  ): StakeholderDto[] => {
    return csvData.map((row) => {
      const language =
        PLATFORM_LANGUAGES.find(
          (lng) => lng.name.toLowerCase() === row.Language?.toLowerCase(),
        )?.id ?? LocalizationLanguage.EN;

      const type =
        row.Type?.toLowerCase() === "internal"
          ? DoubleMaterialityStakeholderType.Internal
          : DoubleMaterialityStakeholderType.External;

      const impactAreas = row["Impact Areas"]?.split(",").map((area) => {
        const trimmedArea = area.trim().toLowerCase();

        switch (trimmedArea) {
          case "risk":
            return DoubleMaterialityStakeholderImpactArea.Risk;
          case "opportunity":
            return DoubleMaterialityStakeholderImpactArea.Opportunity;
          case "other":
            return DoubleMaterialityStakeholderImpactArea.Impact;
          default:
            return DoubleMaterialityStakeholderImpactArea.Impact;
        }
      }) || [DoubleMaterialityStakeholderImpactArea.Impact];

      const esrsAreas = row["ESRS Areas"]?.split(",").map((area) => {
        const trimmedArea = area.trim().toLowerCase();

        switch (trimmedArea) {
          case "climate_change":
            return DoubleMaterialityStakeholderESRSArea.ClimateChange;
          case "pollution":
            return DoubleMaterialityStakeholderESRSArea.Pollution;
          case "water_and_marine_sources":
            return DoubleMaterialityStakeholderESRSArea.WaterAndMarineSources;
          case "biodiversity_and_ecosystems":
            return DoubleMaterialityStakeholderESRSArea.BiodiversityAndEcosystems;
          case "circular_economy":
            return DoubleMaterialityStakeholderESRSArea.CircularEconomy;
          case "own_workforce":
            return DoubleMaterialityStakeholderESRSArea.OwnWorkforce;
          case "workers_in_value_chain":
            return DoubleMaterialityStakeholderESRSArea.WorkersInValueChain;
          case "affected_communities":
            return DoubleMaterialityStakeholderESRSArea.AffectedCommunities;
          case "consumers_and_end_users":
            return DoubleMaterialityStakeholderESRSArea.ConsumersAndEndUsers;
          case "business_conduct":
            return DoubleMaterialityStakeholderESRSArea.BusinessConduct;
          default:
            return DoubleMaterialityStakeholderESRSArea.BusinessConduct;
        }
      }) || [DoubleMaterialityStakeholderESRSArea.BusinessConduct];

      return {
        id: "",
        fullName: row["Full Name"] || "",
        email: row.Email || "",
        companyName: row["Company Name"] || "",
        comment: row.Comment || "",
        companyId: "",
        emailSentAt: null,
        language,
        type,
        impactAreas,
        esrsAreas,
        year: year,
        createdAt: "",
        updatedAt: "",
      } as StakeholderDto;
    });
  };

  const handleFileImport = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const file = event.target.files?.[0];

    if (!file) return;

    if (file.type !== "text/csv") {
      enqueueSnackbar(
        t("stakeholders.import.invalidFormat", "Please select a CSV file"),
        {
          variant: "error",
        },
      );

      return;
    }
    // @ts-expect-error papaparse types are not correct
    parse(file, {
      header: true,
      skipEmptyLines: true,
      trimHeaders: true,
      encoding: "UTF-8",
      complete: (results) => {
        if (results.errors.length > 0) {
          enqueueSnackbar(
            t("stakeholders.import.parseError", "Error parsing CSV file\n") +
            results.errors.map((error) => error.message).join(", \n"),
            {
              variant: "error",
            },
          );

          return;
        }

        const csvData = results.data as Record<string, string>[];
        const stakeholdersData = transformCsvDataToStakeholders(csvData);

        importStakeholders({
          stakeholders: stakeholdersData,
        });
      },
      error: () => {
        enqueueSnackbar(t("stakeholders.import.error", "Failed to import file"), {
          variant: "error",
        });
      },
    });

    event.target.value = "";
  };

  const renderImpactAreaChip = (
    area: StakeholderDto["impactAreas"][number],
  ): JSX.Element => {
    return <MuiChip key={area} label={labelMap[area]} color={colorMap[area]} />;
  };

  const renderLanguage = (languageId: StakeholderDto["language"]): string => {
    return (
      PLATFORM_LANGUAGES.find((lng) => lng.id === languageId)?.name ||
      String(languageId)
    );
  };

  const columns: GridColDef<StakeholderDto>[] = [
    {
      field: "impactAreas",
      headerName: t("stakeholders.table.impactAreas", "IRO"),
      width: 200,
      renderCell: (params: GridRenderCellParams<StakeholderDto, string[]>) => (
        <Stack direction="row" spacing={1.25} flexWrap="wrap" useFlexGap sx={{ my: 1 }}>
          {params.value?.map((area) => renderImpactAreaChip(area as DoubleMaterialityStakeholderImpactArea))}
        </Stack>
      ),
      type: "singleSelect",
      valueOptions: [
        { value: DoubleMaterialityStakeholderImpactArea.Risk, label: labelMap[DoubleMaterialityStakeholderImpactArea.Risk] },
        { value: DoubleMaterialityStakeholderImpactArea.Opportunity, label: labelMap[DoubleMaterialityStakeholderImpactArea.Opportunity] },
        { value: DoubleMaterialityStakeholderImpactArea.Impact, label: labelMap[DoubleMaterialityStakeholderImpactArea.Impact] },
      ],

      valueGetter: (value: DoubleMaterialityStakeholderImpactArea[]) => value,
      sortComparator: (valueA, valueB) =>
        getImpactAreasSortValue(
          valueA as DoubleMaterialityStakeholderImpactArea[] | undefined,
        ).localeCompare(
          getImpactAreasSortValue(
            valueB as DoubleMaterialityStakeholderImpactArea[] | undefined,
          ),
        ),
      filterOperators: iroFilterOperators,
    },
    {
      field: "type",
      headerName: t("stakeholders.table.type", "Stakeholder type"),
      width: 150,
      type: "singleSelect",
      valueOptions: [
        { value: DoubleMaterialityStakeholderType.Internal, label: "Internal" },
        { value: DoubleMaterialityStakeholderType.External, label: "External" },
      ],
    },
    {
      field: "esrsAreas",
      headerName: t("stakeholders.table.esrsAreas", "ESRS area"),
      width: 250,
      type: "singleSelect",
      valueOptions: Object.values(DoubleMaterialityStakeholderESRSArea).map((area) => ({
        value: area,
        label:
          ESRS_TYPES[area] === "OTHER"
            ? "Other"
            : `${ESRS_TYPES[area]}: ${area.replace(/_/g, " ").trim()}`,
      })),
      valueGetter: (value: DoubleMaterialityStakeholderESRSArea[]) => value,
      sortComparator: (valueA, valueB) =>
        getEsrsAreasSortValue(
          valueA as DoubleMaterialityStakeholderESRSArea[] | undefined,
        ).localeCompare(
          getEsrsAreasSortValue(
            valueB as DoubleMaterialityStakeholderESRSArea[] | undefined,
          ),
        ),
      filterOperators: esrsFilterOperators,
      renderCell: (params: GridRenderCellParams<StakeholderDto, DoubleMaterialityStakeholderESRSArea[]>) => {
        const areas = params.value || [];
        const totalAreas = areas.length;
        const maxVisible = totalAreas > 4 ? 3 : 4;
        const visibleAreas = areas.slice(0, maxVisible);
        const hiddenAreas = areas.slice(maxVisible);
        const hasMore = hiddenAreas.length > 0;

        return (
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "repeat(2, auto)",
              gap: 1,
              justifyContent: "start",
              alignItems: "center",
              py: 1
            }}
          >
            {visibleAreas.map((area) => (
              <Chip key={area} label={ESRS_TYPES[area]} type={ESRS_TYPES[area]} />
            ))}
            {hasMore && (
              <Tooltip
                title={hiddenAreas.map((area) => ESRS_TYPES[area]).join(", ")}
              >
                <MuiChip label={`+${hiddenAreas.length}`} size="small" />
              </Tooltip>
            )}
          </Box>
        );
      },
    },
    {
      field: "companyName",
      headerName: t("stakeholders.table.companyName"),
      width: 200,
    },
    {
      field: "fullName",
      headerName: t("stakeholders.table.fullName"),
      width: 200,
    },
    {
      field: "email",
      headerName: t("stakeholders.table.email"),
      width: 250,
    },
    {
      field: "comment",
      headerName: t("stakeholders.table.comment"),
      width: 250,
      renderCell: (params) => (
        <Tooltip title={<div dangerouslySetInnerHTML={{ __html: params.value ?? "" }} />}>
          <Box
            sx={{
              maxWidth: "100%",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
            dangerouslySetInnerHTML={{ __html: params.value ?? "" }}
          />
        </Tooltip>
      ),
    },
    {
      field: "surveyLink",
      headerName: t("stakeholders.table.surveyLink", "Survey link"),
      width: 100,
      align: "center",
      headerAlign: "center",
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Tooltip title={t("common.copy")}>
          <IconButton
            onClick={() => handleCopySurveyLink(params.row)}
            size="small"
            color="primary"
          >
            <FeatherIcon icon="copy" size={16} />
          </IconButton>
        </Tooltip>
      ),
    },
    {
      field: "language",
      headerName: t("stakeholders.table.language", "Language"),
      width: 150,
      valueGetter: (value, row) => renderLanguage(row.language),
    },
    {
      field: "actions",
      headerName: t("stakeholders.table.actions"),
      width: 120,
      align: "center",
      headerAlign: "center",
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Stack direction="row" spacing={1} justifyContent="center" width="100%">
          <IconButton
            onClick={() => handleStakeholderEdit(params.row)}
            color="primary"
            size="small"
          >
            <FeatherIcon icon="edit-3" size={16} />
          </IconButton>
          <IconButton
            onClick={() => handleStakeholderDelete(params.row)}
            color="error"
            size="small"
          >
            <FeatherIcon icon="trash-2" size={16} />
          </IconButton>
        </Stack>
      ),
    },
  ];

  if (isPendingGetStakeholders) {
    return <div>{t("common.loading")}</div>;
  }


  // Custom Toolbar to include existing actions + DataGrid filters
  const CustomToolbar = (): JSX.Element => {
    return (
      <GridToolbarContainer
        sx={{
          display: "flex",
          justifyContent: "space-between",
          p: 2,
        }}
      >
        <Stack
          direction="row"
          spacing={2}
          alignItems="center"
          useFlexGap
          sx={{ flexWrap: "wrap" }}
        >
          <GridToolbarQuickFilter />
          <GridToolbarFilterButton />
        </Stack>
        <Stack direction="row" spacing={1}>
          <Button
            variant="text"
            startIcon={<FeatherIcon icon="plus" size={16} />}
            onClick={onAddStakeholder}
            sx={{
              textTransform: "none",
              fontWeight: 600,
            }}
          >
            {t("stakeholders.addStakeholder", "Add stakeholder")}
          </Button>

          <Button
            variant="text"
            startIcon={<FeatherIcon icon="upload" size={16} />}
            onClick={handleImportClick}
            disabled={isPendingImportStakeholders}
            sx={{
              textTransform: "none",
              fontWeight: 600,
            }}
          >
            {isPendingImportStakeholders
              ? t("common.loading", "Loading...")
              : t("stakeholders.import.title", "Import")}
          </Button>

          <Button
            variant="text"
            startIcon={<FeatherIcon icon="download" size={16} />}
            onClick={handleExportCSV}
            sx={{
              textTransform: "none",
              fontWeight: 600,
            }}
          >
            {t("stakeholders.export.title", "Export")}
          </Button>
        </Stack>
      </GridToolbarContainer>
    );
  };






  // Hidden file input for CSV import

  return (
    <Box sx={{ p: 3 }}>
      <input
        ref={fileInputRef}
        type="file"
        accept=".csv"
        onChange={handleFileImport}
        style={{ display: "none" }}
      />

      {/* Table */}
      {/* DataGrid */}
      <DataGrid
        rows={rows}
        columns={columns}
        loading={isPendingGetStakeholders}
        hideFooterPagination
        initialState={{
          filter: {
            filterModel: {
              items: [],
              logicOperator: GridLogicOperator.Or,
            },
          },
        }}
        slots={{
          toolbar: CustomToolbar,
          noRowsOverlay: () => (
            <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100%" }}>
              <DataPlaceholder onAddStakeholder={onAddStakeholder} />
            </Box>
          )
        }}
        // this has to be defined inline to work properly, not sure why
        slotProps={{
          panel: {
            sx: {
              "& .MuiDataGrid-filterForm": {
                display: "flex",
                flexDirection: "row",
                flexWrap: "nowrap",
                alignItems: "baseline",
                gap: 2,
                p: 2,
              },
              "& .MuiDataGrid-filterFormDeleteIcon": {
                order: -1,
                alignSelf: "center",
              },
              "& .MuiDataGrid-filterFormColumnInput": {
                minWidth: 150,
              },
              "& .MuiDataGrid-filterFormOperatorInput": {
                minWidth: 120,
              },
              "& .MuiDataGrid-filterFormValueInput": {
                minWidth: 220,
              },
              "& .MuiDataGrid-filterForm .MuiFormControl-root": {
                marginTop: 0,
                marginBottom: 0,
              },
              "& .MuiDataGrid-filterForm .MuiFormLabel-root": {
                display: "none",
              },
              "& .MuiDataGrid-filterForm .MuiInputBase-root": {
                marginTop: 0,
              },
              "& .MuiDataGrid-filterFormValueInput input.MuiInput-input": {
                paddingBottom: "20px",
              },
            },
          },
        }}
        getRowHeight={() => 'auto'}
        sx={{
          border: 0,
          '& .MuiDataGrid-cell': {
            display: 'flex',
            alignItems: 'center',
            py: 1
          },
          "& .MuiDataGrid-columnHeader": {
            backgroundColor: "#f5f5f5",
          },

        }}
        disableRowSelectionOnClick
      />
    </Box>
  );
};

export default StakeholdersTable;
